import { IContent } from "@vmosolutions/oc-interfaces";
import { useMemo } from "react";
import Papa from "papaparse";
import { Alert, Box, styled } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import { useFileContents } from "src/pages/contentViewers/useFileContents";
import { ViewerShell } from "src/pages/contentViewers/ViewerShell";

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_KEY as string);

interface ICSVViewerProps {
  windowProps: {
    file: IContent;
  };
}

interface ICSVTable {
  columns: GridColDef[];
  rows: Array<Record<string, string | number>>;
}

const CSVBox = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  "& .csvGridContainer": {
    flex: 1,
    minHeight: 0,
  },
}));

/**
 * Builds DataGrid columns/rows from a parsed CSV (array of string arrays).
 * The first row is treated as the header; remaining rows become grid rows
 * with a synthetic `id` (their row index).
 */
const buildTable = (data: string[][]): ICSVTable => {
  if (!data.length) {
    return { columns: [], rows: [] };
  }
  const [header, ...body] = data;
  const columns: GridColDef[] = header.map((title, index) => ({
    field: `col${index}`,
    headerName: title?.trim() === "" ? `Column ${index + 1}` : title,
    flex: 1,
    minWidth: 120,
  }));
  const rows = body.map((cells, rowIndex) => {
    const row: Record<string, string | number> = { id: rowIndex };
    header.forEach((_col, colIndex) => {
      row[`col${colIndex}`] = cells[colIndex] ?? "";
    });
    return row;
  });
  return { columns, rows };
};

export function CSVViewerWindow(props: ICSVViewerProps): JSX.Element {
  const { file } = props.windowProps;
  const { data: text, loading, error } = useFileContents(file, "text");

  const table = useMemo<ICSVTable>(() => {
    if (text === null) return { columns: [], rows: [] };
    return buildTable(Papa.parse<string[]>(text, { skipEmptyLines: true }).data);
  }, [text]);

  const hasData = table.columns.length > 0;

  return (
    <ViewerShell file={file} loading={loading} error={error}>
      <CSVBox>
        <Box className="csvGridContainer">
          <DataGridPro
            columns={table.columns}
            rows={table.rows}
            density="compact"
            disableRowSelectionOnClick
            pagination
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          />
        </Box>
        {!loading && text !== null && !hasData && (
          <Alert severity="info" variant="filled" sx={{ mt: 1 }}>
            This file is empty or contains no readable rows.
          </Alert>
        )}
      </CSVBox>
    </ViewerShell>
  );
}
